import React, { useEffect, useState } from 'react';

import Link from 'next/link';
import { useRouter } from 'next/router';

import type { NextPage } from 'next';

import { DateTime } from 'luxon';
import { useRecoilValue } from 'recoil';

import { useAnnouncements } from '@hooks_iqfu/dashboard/useAnnouncements';
import { useDashboardData } from '@hooks_iqfu/dashboard/useDashboardData';
import { SaleCase, useSaleCase } from '@hooks_iqfu/dashboard/useSaleCase';

import ConsoleSimulatorSearchArea from '@components_iqfu/dashboard/console_simulator/ConsoleSimulatorSearchArea';
import SaleCasesMonthlyCounts from '@components_iqfu/dashboard/sale_case_monthly_counts/SaleCasesMonthlyCounts';

import { companyLoginUserDataAtom, companyLoginUserDataSelector } from '@recoil_iqfu/atoms/auth/companyLoginUserData';

import { AnnouncementsType } from '@type-def_iqfu/AnnouncementsType';
import { DashboardCompanyCountType, SaleCasesMonthlyCountsType } from '@type-def_iqfu/DashboardType';

import DashboardPageContainer from '@components_common/page_container/DashboardPageContainer';
import {
  DateOutputFormat,
  DateOutputFormatWithColon,
  DealType,
  PropertyType,
  SaleCasesStatusType,
} from '@constants_iqfu';
import styles from '@styles_iqfu/dashboard/dashboard.module.scss';

const Dashboard: NextPage = () => {
  const router = useRouter();
  const { getCompanyDashboardData, getSaleCasesMonthlyCounts } = useDashboardData();
  const { getAnnouncementsData } = useAnnouncements();
  const { getSaleCases } = useSaleCase();

  const companyLoginUserData = useRecoilValue(companyLoginUserDataAtom);
  const { companyId, isMultipleCompanyUser } = useRecoilValue(companyLoginUserDataSelector);

  const [dashboardCount, setDashboardCount] = useState<DashboardCompanyCountType>();
  const [saleCasesMonthlyCounts, setSaleCasesMonthlyCounts] = useState<SaleCasesMonthlyCountsType[]>([]);
  const [announcements, setAnnouncements] = useState<AnnouncementsType[]>([]);
  const [saleCases, setSaleCases] = useState<SaleCase[]>();

  const serviceList = companyLoginUserData?.services;

  const iqfuAccessibleAuth = () => {
    return serviceList?.includes('iqfu');
  };

  const proAccessibleAuth = () => {
    return serviceList?.includes('pro');
  };

  useEffect(() => {
    if (iqfuAccessibleAuth()) {
      fetchCompanies();
      fetchAnnouncements();
      fetchSaleCasesMonthlyCounts();
      fetchSalesCase();
    } else if (proAccessibleAuth()) {
      router.replace(`/console/company/iqfu_pro`);
    }
  }, []);

  const fetchCompanies = async () => {
    const result = await getCompanyDashboardData();
    setDashboardCount(result as DashboardCompanyCountType);
  };

  const fetchSaleCasesMonthlyCounts = async () => {
    const result = await getSaleCasesMonthlyCounts('company');
    console.log('getSaleCasesMonthlyCounts', result);
    setSaleCasesMonthlyCounts(result);
  };

  const fetchAnnouncements = async () => {
    const result = await getAnnouncementsData('company', {
      offset: 0,
      limit: 4,
      latest: true,
      destination: 'company',
    });
    console.log('getAnnouncementsData', result);
    setAnnouncements(result.announcements);
  };

  const fetchSalesCase = async () => {
    const result = await getSaleCases('company', { offset: 0, limit: 5, status: 'approved' });
    console.log('getSaleCases', result);
    setSaleCases(result.sale_cases);
  };

  return (
    <DashboardPageContainer userType="company">
      {iqfuAccessibleAuth() && (
        <>
          {dashboardCount && dashboardCount.rejected_sale_case_count > 0 && (
            <div className={styles.info_wrap}>
              <div className={styles.info_title}>
                <h4 className={styles.title_forward}>差し戻し売却実績</h4>
                <h4 className={styles.company_request_counter}>{dashboardCount.rejected_sale_case_count}</h4>
                <h4>件あります</h4>
              </div>
              <Link href={'/console/company/sale_cases?status=rejected'}>
                <a className={styles.link_text}>差し戻し売却実績一覧</a>
              </Link>
            </div>
          )}

          {dashboardCount && dashboardCount.rejected_company_profile_image_count > 0 && !isMultipleCompanyUser && (
            <div className={styles.info_wrap}>
              <div className={styles.info_title}>
                <h4 className={styles.title_forward}>差し戻し紹介写真</h4>
                <h4 className={styles.company_request_counter}>
                  {dashboardCount.rejected_company_profile_image_count}
                </h4>
                <h4>件あります</h4>
              </div>
              <Link href={`/console/company/company_detail/${companyId}/profile_images`}>
                <a className={styles.link_text}>差し戻し紹介写真一覧</a>
              </Link>
            </div>
          )}

          {dashboardCount && dashboardCount.rejected_company_profile_image_count > 0 && isMultipleCompanyUser && (
            <div className={styles.info_wrap}>
              <div className={styles.info_title}>
                <h4 className={styles.title_forward}>差し戻し紹介写真</h4>
                <h4 className={styles.company_request_counter}>
                  {dashboardCount.rejected_company_profile_image_count}
                </h4>
                <h4>件あります</h4>
              </div>
              <Link href={`/console/company/companies_list?rejected=true`}>
                <a className={styles.link_text}>差し戻し紹介写真一覧</a>
              </Link>
            </div>
          )}

          {dashboardCount && dashboardCount.rejected_company_profile_count > 0 && !isMultipleCompanyUser && (
            <div className={styles.info_wrap}>
              <div className={styles.info_title}>
                <h4>差し戻し紹介文があります</h4>
              </div>
              <Link href={`/console/company/company_detail/${companyId}/profile`}>
                <a className={styles.link_text}>差し戻し紹介文はこちら</a>
              </Link>
            </div>
          )}

          {dashboardCount && dashboardCount.rejected_company_profile_count > 0 && isMultipleCompanyUser && (
            <div className={styles.info_wrap}>
              <div className={styles.info_title}>
                <h4>差し戻し紹介文があります</h4>
              </div>
              <Link href={`/console/company/companies_list?rejected=true`}>
                <a className={styles.link_text}>差し戻し紹介文一覧</a>
              </Link>
            </div>
          )}
          {dashboardCount && dashboardCount.rejected_disclosure_statement_count > 0 && (
            <div className={styles.info_wrap}>
              <div className={styles.info_title}>
                <h4 className={styles.title_forward}>差し戻し重説</h4>
                <h4 className={styles.company_request_counter}>{dashboardCount.rejected_disclosure_statement_count}</h4>
                <h4>件あります</h4>
              </div>
              <Link href={`/console/company/iqra_cloud/jusetsu/list?status=rejected`}>
                <a className={styles.link_text}>差し戻し重説一覧</a>
              </Link>
            </div>
          )}

          <div className={styles.announcement_wrapper}>
            <h3 className={styles.section_title}>お知らせ</h3>
            {announcements && announcements.length > 0 ? (
              announcements.map((data, i) => (
                <Link key={i} href={`/console/company/announcements/${data.id}`} passHref>
                  <div className={styles.announcement_row}>
                    <div className={styles.flex}>
                      <div className={styles.list_date}>
                        {data.publish_at && DateTime.fromISO(data.publish_at).toFormat(DateOutputFormatWithColon)}
                      </div>
                      <div className={styles.list_title}>{data.title}</div>
                    </div>
                    <img src="/images/iqfu/dashboard/arrow-right.svg" alt="arrow" />
                  </div>
                </Link>
              ))
            ) : (
              <div>新着のお知らせはありません</div>
            )}
          </div>

          <div className={styles.announcement_page_link}>
            <Link href={`/console/company/announcements`} passHref>
              お知らせ一覧
            </Link>
          </div>

          <ConsoleSimulatorSearchArea isTopPage />

          <div className={styles.title_wrap}>
            <h3 className={styles.section_title}>最近登録した売却実績</h3>
          </div>

          {saleCases && saleCases.length > 0 ? (
            saleCases.map((saleCase: SaleCase, i: number) => (
              <div key={i} className={styles.case_wrapper}>
                <div>
                  <div className={styles.sale_case_flex}>
                    <div className={styles.content_wrap}>
                      <div className={`${styles.status_table_label} ${styles[saleCase.status]}`}>
                        {SaleCasesStatusType[saleCase.status]}
                      </div>
                      <div className={styles.status_table_label}>{saleCase.is_public ? '公開' : '非公開'}</div>
                    </div>
                    <div className={styles.table_item}>{PropertyType[saleCase.property_type]}</div>
                    <div className={styles.table_address}>{saleCase.full_address}</div>
                  </div>
                  <div className={styles.content_wrap}>
                    <div className={styles.table_item}>
                      {isMultipleCompanyUser && (
                        <span className={styles.recent_sale_case_company_name}>{saleCase.company_name}</span>
                      )}
                      {saleCase.contract_date
                        ? DateTime.fromISO(saleCase.contract_date).toFormat(DateOutputFormat)
                        : ''}
                    </div>
                    <div className={styles.table_item}>{DealType[saleCase.deal_type]}</div>
                    <div className={styles.table_item}>
                      {saleCase.price ? saleCase.price.toLocaleString() + '万円' : ''}
                    </div>
                  </div>
                </div>

                <Link href={`/console/company/sale_cases/${saleCase.id}`} passHref>
                  <a className={styles.sale_case_edit_button}>詳細</a>
                </Link>
              </div>
            ))
          ) : (
            <div>最近登録が完了した売却実績が存在しません</div>
          )}

          <div className={styles.title_wrap}>
            <h3 className={styles.section_title}>直近一年の売却実績の登録件数</h3>
          </div>
          <SaleCasesMonthlyCounts saleCasesMonthlyCounts={saleCasesMonthlyCounts} />
        </>
      )}
    </DashboardPageContainer>
  );
};

export default Dashboard;
